import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { db } from '../db/schema'
import { setupVault, unlockVault, rewrapVault } from '../crypto/keys'

interface AuthState {
  isUnlocked: boolean
  isInitialized: boolean
  checked: boolean
  dek: Uint8Array | null
}

interface AuthContextValue extends AuthState {
  setup: (password: string) => Promise<void>
  unlock: (password: string) => Promise<void>
  lock: () => void
  refresh: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isUnlocked: false,
    isInitialized: false,
    checked: false,
    dek: null,
  })

  const refresh = useCallback(async () => {
    const vault = await db.vault.get('singleton')
    setState(s => ({ ...s, isInitialized: !!vault, checked: true }))
  }, [])

  const setup = useCallback(async (password: string) => {
    const existing = await db.vault.get('singleton')
    if (existing) throw new Error('Vault già inizializzato')
    const { material, dek } = await setupVault(password)
    await db.vault.put({
      id: 'singleton',
      salt: material.salt,
      wrappedDEK: material.wrappedDEK,
      verification: material.verification,
      createdAt: Date.now(),
    })
    setState({ isUnlocked: true, isInitialized: true, checked: true, dek })
  }, [])

  const unlock = useCallback(async (password: string) => {
    const vault = await db.vault.get('singleton')
    if (!vault) throw new Error('Vault non inizializzato')
    const dek = await unlockVault(password, {
      salt: vault.salt,
      wrappedDEK: vault.wrappedDEK,
      verification: vault.verification,
    })
    setState({ isUnlocked: true, isInitialized: true, checked: true, dek })
  }, [])

  const lock = useCallback(() => {
    setState(s => ({ ...s, isUnlocked: false, dek: null }))
  }, [])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const vault = await db.vault.get('singleton')
    if (!vault) throw new Error('Vault non inizializzato')
    // Verifica la password corrente recuperando la DEK
    const dek = await unlockVault(currentPassword, {
      salt: vault.salt,
      wrappedDEK: vault.wrappedDEK,
      verification: vault.verification,
    })
    // Riavvolge la stessa DEK con la nuova password
    const material = await rewrapVault(newPassword, dek)
    await db.vault.put({
      ...vault,
      salt: material.salt,
      wrappedDEK: material.wrappedDEK,
      verification: material.verification,
    })
    // La DEK in memoria resta valida: la sessione corrente continua
    setState(s => ({ ...s, dek }))
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, setup, unlock, lock, refresh, changePassword }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider')
  return ctx
}
